import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
 ArrowRight,
 TrendingDown,
 ShieldCheck,
 Sparkles,
 Check,
 Minus,
 Zap,
 Gauge,
 Smartphone,
 Clock,
 Truck,
 Star,
 Trophy,
 Award,
 LineChart,
 Repeat,
 MessageSquare,
 Eye,
 DollarSign,
 ShoppingBag,
 Activity,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';

/**
 * NoFeeBrand — official brand-mark split: "No" white + "Fee" gold.
 * Used in copy where the wordmark needs the official two-tone treatment.
 */
const NoFeeBrand = ({ className = '' }) => (
 <span className={className}>
 <span className="text-white">No</span>
 <span className="text-gold-400">Fee</span>
 </span>
);
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

/**
 * SavingsCalculator — premium, calm, financial-insight tone.
 *
 * Aesthetic: large gold savings figure, subtle red sublabel for commission loss.
 * NO aggressive marketing. NO emoji. NO red screaming numbers.
 *
 * Reused for two routes (canonical = /savings-calculator):
 * - /savings-calculator (primary, Google Ads target)
 * - /for-restaurants/savings (in-funnel hierarchical path)
 */

const COMPETITORS = [
 { id: 'typical', label: 'Typical Marketplace', rate: 30, color: 'text-rose-300' },
 { id: 'premium', label: 'Premium Marketplace', rate: 30, color: 'text-emerald-300' },
 { id: 'highfee', label: 'High-Fee Marketplace', rate: 28, color: 'text-orange-300' },
 { id: 'custom', label: 'Custom Commission', rate: 25, color: 'text-white/70' },
];

const NOFEE_PRICE = 299;

const formatMoney = (n) =>
 n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const SliderRow = ({ label, value, onChange, min, max, step, unit, testId }) => (
 <div data-testid={`field-${testId}`}>
 <div className="flex items-baseline justify-between mb-2">
 <label className="text-[12px] uppercase tracking-[0.22em] text-white/55 font-bold">
 {label}
 </label>
 <span className="text-2xl sm:text-3xl font-black text-white tabular-nums">
 {unit === '$'? formatMoney(value): value.toLocaleString('en-US')}
 {unit && unit!== '$'? <span className="text-white/40 text-base ml-1">{unit}</span>: null}
 </span>
 </div>
 <input
 type="range"
 min={min}
 max={max}
 step={step}
 value={value}
 onChange={(e) => onChange(Number(e.target.value))}
 data-testid={`slider-${testId}`}
 className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 accent-gold-500"
 />
 </div>
);

const SavingsCalculatorPage = () => {
 const [orders, setOrders] = useState(300);
 const [ticket, setTicket] = useState(30);
 const [competitor, setCompetitor] = useState(COMPETITORS[0]);
 const [customRate, setCustomRate] = useState(25);

 const rate = competitor.id === 'custom'? customRate: competitor.rate;

 const calc = useMemo(() => {
 const monthlyRevenue = orders * ticket;
 const monthlyLoss = monthlyRevenue * (rate / 100);
 const yearlyLoss = monthlyLoss * 12;
 const monthlySaved = monthlyLoss - NOFEE_PRICE;
 const yearlySaved = monthlySaved * 12;
 const dinners = Math.max(0, Math.floor(monthlySaved / 75)); // $75 avg family dinner
 return {
 monthlyRevenue,
 monthlyLoss,
 yearlyLoss,
 monthlySaved: Math.max(0, monthlySaved),
 yearlySaved: Math.max(0, yearlySaved),
 dinners,
 };
 }, [orders, ticket, rate]);

 const jsonLd = {
 '@context': 'https://schema.org',
 '@type': 'FAQPage',
 'mainEntity': [
 {
 '@type': 'Question',
 'name': 'How much commission do food-delivery marketplaces charge restaurants?',
 'acceptedAnswer': {
 '@type': 'Answer',
 'text': 'Typical food-delivery marketplaces charge significant per-order commissions. On $9,000 of monthly orders, that can add up to thousands per month in lost margin — before any payout-acceleration, promotional, or visibility fees.',
 },
 },
 {
 '@type': 'Question',
 'name': 'How much does NoFee cost?',
 'acceptedAnswer': {
 '@type': 'Answer',
 'text': 'NoFee charges a flat $299/month for unlimited orders with 0% commission. Restaurants receive direct payouts from Stripe.',
 },
 },
 {
 '@type': 'Question',
 'name': 'Do restaurants really save money switching to NoFee?',
 'acceptedAnswer': {
 '@type': 'Answer',
 'text': 'A restaurant doing 200+ orders per month at $25 average ticket typically saves $1,000-$3,000/month vs traditional commission marketplaces, depending on volume.',
 },
 },
 ],
 };

 return (
 <LanguageProvider>
 <div className="min-h-screen bg-black text-white">
 <SEO
 title="Restaurant Savings Calculator — How Much Are You Losing to Commissions?"
 description="Calculate how much your restaurant loses every month to food-delivery marketplace commissions. NoFee charges 0% — flat $299/month. See your real numbers in 10 seconds."
 path="/savings-calculator"
 jsonLd={jsonLd}
 />
 <Navbar />

 {/* Hero */}
 <section className="relative pt-32 sm:pt-36 lg:pt-44 pb-10 lg:pb-14">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.08),transparent_60%)]" />
 <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/[0.04] mb-6">
 <Sparkles className="w-3.5 h-3.5 text-gold-300" />
 <span className="text-[11px] uppercase tracking-[0.24em] text-gold-300 font-semibold">
 Savings Calculator
 </span>
 </div>
 <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
 How much are you
 <br />
 <span className="bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent">
 paying in commissions?
 </span>
 </h1>
 <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-white/65 leading-relaxed">
 A calm, honest look at the money flowing out of your restaurant every month.
 Change the three numbers below — the difference is your decision.
 </p>
 </div>
 </section>

 {/* Calculator */}
 <section className="relative pb-16 lg:pb-20" data-testid="calculator">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
 {/* Inputs */}
 <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-7 sm:p-8 space-y-7">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 Your restaurant
 </p>

 <SliderRow
 label="Orders per month"
 value={orders}
 onChange={setOrders}
 min={50}
 max={2000}
 step={10}
 testId="orders"
 />

 <SliderRow
 label="Average ticket"
 value={ticket}
 onChange={setTicket}
 min={10}
 max={120}
 step={1}
 unit="$"
 testId="ticket"
 />

 {/* Competitor selector */}
 <div data-testid="field-competitor">
 <p className="text-[12px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">
 Current marketplace
 </p>
 <div className="grid grid-cols-2 gap-2">
 {COMPETITORS.map((c) => (
 <button
 key={c.id}
 onClick={() => setCompetitor(c)}
 data-testid={`competitor-${c.id}`}
 className={`px-3 py-3 rounded-2xl border text-left transition-all ${
 competitor.id === c.id
? 'border-gold-500/60 bg-gold-500/[0.06]'
: 'border-white/10 hover:border-white/20 bg-white/[0.02]'
 }`}
 >
 <div className={`text-[13px] font-bold ${competitor.id === c.id? 'text-white': 'text-white/75'}`}>
 {c.label}
 </div>
 <div className="text-[11px] text-white/45 mt-0.5 tabular-nums">
 {c.id === 'custom'? `${customRate}% commission`: `${c.rate}% commission`}
 </div>
 </button>
 ))}
 </div>
 {competitor.id === 'custom' && (
 <div className="mt-4">
 <SliderRow
 label="Custom commission %"
 value={customRate}
 onChange={setCustomRate}
 min={5}
 max={40}
 step={1}
 unit="%"
 testId="custom-rate"
 />
 </div>
 )}
 </div>
 </div>

 {/* Output — premium gold + subtle red sublabel */}
 <div
 className="lg:col-span-3 relative rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-7 sm:p-10 overflow-hidden"
 data-testid="results"
 >
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(212,175,55,0.10),transparent_60%)]" />
 <div className="relative">
 <div className="flex items-center justify-between mb-6">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 Estimated savings with NoFee
 </p>
 <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 font-bold">
 Per year
 </span>
 </div>

 {/* Hero number — yearly savings in gold */}
 <div className="space-y-1.5">
 <div
 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent tabular-nums leading-none"
 data-testid="yearly-savings"
 >
 {formatMoney(calc.yearlySaved)}
 </div>
 <div className="flex items-center gap-1.5 text-[12px] text-rose-300/80" data-testid="yearly-loss-sublabel">
 <TrendingDown className="w-3 h-3" />
 <span className="tabular-nums">
 Currently lost to commissions: {formatMoney(calc.yearlyLoss)}/yr
 </span>
 </div>
 </div>

 {/* Secondary stats */}
 <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
 <Stat
 label="Monthly savings"
 value={formatMoney(calc.monthlySaved)}
 testId="monthly-savings"
 />
 <Stat
 label="Lost / month"
 value={formatMoney(calc.monthlyLoss)}
 muted
 testId="monthly-loss"
 />
 <Stat
 label="Effective rate"
 value={`${rate}%`}
 muted
 testId="effective-rate"
 />
 </div>

 {/* Humanized line */}
 {calc.dinners > 0 && (
 <p className="mt-7 text-[13.5px] text-white/65 leading-relaxed border-t border-white/[0.06] pt-6">
 <span className="text-white font-bold">
 That's {calc.dinners} family dinners
 </span>{' '}
 worth of margin every month — going back into your business instead of a marketplace.
 </p>
 )}

 {/* CTA row */}
 <div className="mt-7 flex flex-col sm:flex-row gap-3">
 <Link
 to="/start?plan=ordering&utm_source=savings_calc"
 data-testid="cta-start-trial"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
 >
 Stop losing {formatMoney(calc.monthlyLoss)} / month
 <ArrowRight className="w-4 h-4" />
 </Link>
 <Link
 to="/for-restaurants/why-nofee"
 data-testid="cta-why-nofee"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-white/15 hover:border-gold-500/50 text-white/80 hover:text-gold-200 font-bold transition-all"
 >
 Why restaurants switch
 </Link>
 </div>

 <p className="mt-4 text-[11px] text-white/35 leading-relaxed">
 Estimates based on $299/mo NoFee subscription. 0% commission · 45-day free trial · no contracts.
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Comparison row */}
 <section className="relative pb-12 lg:pb-16">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 {/* Intro: The Real Cost of Marketplaces */}
 <div className="max-w-3xl mx-auto text-center mb-12">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 The hidden math
 </p>
 <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
 The Real Cost of Marketplaces
 </h2>
 <p className="mt-4 text-white/65 leading-relaxed text-[15px]">
 Most restaurants focus only on commission fees. The real cost often includes:
 </p>
 <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-left text-[14px] text-white/75 max-w-xl mx-auto">
 {[
 'Per-order commissions',
 'Payout delays',
 'Payout acceleration fees',
 'Promotional fees',
 'Visibility fees',
 'Loss of customer ownership',
 'Loss of customer data',
 ].map((b) => (
 <li key={b} className="flex items-start gap-2">
 <Minus className="w-3.5 h-3.5 text-rose-400/70 flex-shrink-0 mt-1" />
 <span>{b}</span>
 </li>
 ))}
 </ul>
 <p className="mt-5 text-[14.5px] text-white/65 leading-relaxed">
 NoFee helps restaurants keep more revenue while maintaining full control of their business.
 </p>
 </div>

 <div className="text-center mb-8">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 Side by side
 </p>
 <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
 Marketplaces vs. NoFee
 </h2>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 <ComparisonCard
 title={competitor.label}
 rate={`${rate}%`}
 isLoss
 bullets={[
 'High per-order commissions',
 'Additional payout acceleration fees',
 'Additional promotional fees',
 'Additional visibility fees',
 'Delayed payout schedules',
 'Marketplace owns customer relationship',
 'Marketplace owns customer data',
 'Customer loyalty belongs to marketplace',
 'No Fast by NoFee recognition',
 'No customer-verified on-time delivery',
 'Limited branding control',
 'Limited direct communication with customers',
 ]}
 />
 <ComparisonCard
 title="NoFee"
 rate="0%"
 bullets={[
 '0% commission on orders',
 'Direct Stripe payouts',
 'Direct payouts',
 'Full payout visibility',
 'Faster access to your revenue',
 'Restaurant owns customer relationship',
 'Restaurant owns customer data',
 'Restaurant owns loyalty program',
 'Branded ordering page',
 '⚡ Fast by NoFee',
 'Verified on-time delivery',
 'Customer-verified delivery confirmations',
 'Delivery performance dashboard',
 'Direct communication with customers',
 ]}
 />
 </div>

 {/* Outro: Why Restaurants Switch to NoFee */}
 <div className="max-w-3xl mx-auto text-center mt-16">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 Beyond savings
 </p>
 <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
 Why Restaurants Switch to NoFee
 </h2>
 <p className="mt-4 text-white/65 leading-relaxed text-[15px]">
 NoFee is not only about saving money. Restaurants also gain:
 </p>
 <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-left text-[14px] text-white/80 max-w-xl mx-auto">
 {[
 'Direct customer relationships',
 'Customer ownership',
 'Direct Stripe payouts',
 'Stronger branding',
 'Loyalty ownership',
 'Delivery performance visibility',
 'Fast by NoFee recognition',
 'Customer-verified trust',
 ].map((b) => (
 <li key={b} className="flex items-start gap-2">
 <Check className="w-3.5 h-3.5 text-gold-400 flex-shrink-0 mt-1" />
 <span>{b}</span>
 </li>
 ))}
 </ul>
 <p className="mt-5 text-[14.5px] text-white/65 leading-relaxed">
 NoFee helps restaurants grow without depending on third-party marketplaces.
 </p>
 </div>
 </div>
 </section>

 {/* ── ⚡ Fast by NoFee — Owner & Manager benefits ──
 Sits between "Why Restaurants Switch to NoFee" and
 "Restaurant Dashboard Benefits". This section reframes
 Fast as an OPERATIONAL CONTROL system, not just a
 delivery badge. Keeps the black + gold treatment of the
 page; uses the official two-tone NoFee wordmark. */}
 <section className="relative pb-24 lg:pb-32" data-testid="fast-owner-benefits-section">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 {/* Title */}
 <div className="text-center max-w-3xl mx-auto mb-10">
 <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.18em] mb-4">
 <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
 Fast by <NoFeeBrand />
 </div>
 <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1]">
 <Zap className="inline w-7 h-7 sm:w-9 sm:h-9 text-yellow-400 fill-yellow-400 mr-1 -translate-y-0.5" />
 Fast by <NoFeeBrand />™
 </h2>
 <p className="mt-3 text-[15px] sm:text-base text-gold-300/90 font-semibold">
 Restaurant Performance &amp; Delivery Control System
 </p>
 <p className="mt-5 text-white/65 leading-relaxed text-[15px]">
 Take control of your restaurant operation in real time.
 <br />
 Fast by <NoFeeBrand /> gives restaurant owners and managers complete visibility over delivery performance, customer satisfaction, operational efficiency, and restaurant reputation — even when they are not physically at the restaurant.
 </p>
 </div>

 {/* Owner & Manager benefits — 3-col grid on desktop, stacks
 cleanly on mobile. Each card matches the existing
 marketplace/NoFee card aesthetic from the comparison
 section: subtle gold border, soft black background. */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-12" data-testid="fast-owner-benefits-grid">
 {[
 { Icon: Gauge, title: 'Real-Time Delivery Dashboard', body: 'Monitor deliveries and operational performance live from anywhere.' },
 { Icon: Smartphone, title: 'Manage Your Restaurant From Anywhere', body: 'Owners and managers can monitor restaurant performance remotely 24/7 — traveling, working from home, or managing multiple locations.' },
 { Icon: Clock, title: 'Monitor On-Time Delivery Performance', body: 'See exactly which deliveries arrived on time and which were delayed.' },
 { Icon: Truck, title: 'Identify Delivery Problems Quickly', body: 'Detect bottlenecks and operational issues before they become customer complaints.' },
 { Icon: Star, title: 'Track Fast Score™', body: "Monitor your restaurant's delivery performance rating." },
 { Icon: Trophy, title: 'Verified On-Time Deliveries', body: 'Build credibility through delivery confirmations from real customers.' },
 { Icon: Award, title: 'Fast by NoFee Recognition', body: 'Restaurants with excellent delivery performance earn additional trust and visibility.' },
 { Icon: LineChart, title: 'Measure Team Performance', body: 'Understand how kitchen operations, order preparation, and delivery execution are performing every day.' },
 { Icon: Repeat, title: 'Increase Repeat Customers', body: 'Reliable delivery performance creates customer confidence and encourages repeat business.' },
 { Icon: MessageSquare,title: 'Customer-Verified Feedback', body: 'Receive real customer delivery feedback instead of relying on assumptions.' },
 { Icon: Eye, title: 'Complete Operational Visibility', body: 'Owners no longer need to be physically present to understand what is happening inside the business.' },
 ].map(({ Icon, title, body }) => (
 <div
 key={title}
 className="rounded-2xl border border-yellow-400/20 bg-white/[0.02] hover:border-yellow-400/45 hover:bg-yellow-400/[0.04] transition-colors p-5"
 >
 <span className="w-10 h-10 rounded-xl bg-yellow-400/15 border border-yellow-400/35 flex items-center justify-center mb-3">
 <Icon className="w-5 h-5 text-yellow-400" />
 </span>
 <h3 className="text-[15px] font-black text-white leading-tight">{title}</h3>
 <p className="text-[13.5px] text-white/65 mt-1.5 leading-relaxed">{body}</p>
 </div>
 ))}
 </div>

 {/* What makes Fast different — 4-row 2-col contrast block */}
 <div className="rounded-3xl border border-white/10 bg-zinc-950/70 p-7 sm:p-10 mb-12" data-testid="fast-vs-marketplace">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold text-center">
 The difference
 </p>
 <h3 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight text-center">
 What Makes Fast Different?
 </h3>
 <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-3xl mx-auto">
 {[
 ['Traditional marketplaces show orders.', <>Fast by <NoFeeBrand /> shows operational performance.</>],
 ['Traditional marketplaces show revenue.', <>Fast by <NoFeeBrand /> shows restaurant efficiency.</>],
 ['Traditional marketplaces process deliveries.', <>Fast by <NoFeeBrand /> helps restaurants improve deliveries.</>],
 ['Traditional marketplaces collect data.', <>Fast by <NoFeeBrand /> gives restaurant owners actionable insights.</>],
 ].map(([left, right], i) => (
 <React.Fragment key={i}>
 <p className="text-[13.5px] text-white/55 italic md:text-right md:border-r md:border-white/10 md:pr-6">
 {left}
 </p>
 <p className="text-[14.5px] text-white font-semibold">
 {right}
 </p>
 </React.Fragment>
 ))}
 </div>
 </div>

 {/* Dashboard features — visual chips */}
 <div className="text-center mb-6">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 In your dashboard
 </p>
 <h3 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">
 Everything you need to grow
 </h3>
 </div>
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-12" data-testid="fast-dashboard-chips">
 {[
 { Icon: ShoppingBag, label: 'Orders' },
 { Icon: DollarSign, label: 'Revenue' },
 { Icon: Star, label: 'Fast Score™' },
 { Icon: Trophy, label: 'Verified On-Time Deliveries' },
 { Icon: Award, label: 'Fast by NoFee Recognition' },
 { Icon: Truck, label: 'Delivery Performance' },
 { Icon: Clock, label: 'On-Time Delivery Rate' },
 { Icon: Activity, label: 'Delivery Delay Reports' },
 { Icon: MessageSquare,label: 'Customer Reviews' },
 { Icon: Repeat, label: 'Repeat Customer Metrics' },
 { Icon: Sparkles, label: 'Customer Satisfaction Trends' },
 { Icon: LineChart, label: 'Restaurant Performance Analytics' },
 ].map(({ Icon, label }) => (
 <div
 key={label}
 className="rounded-xl border border-white/10 bg-white/[0.02] hover:border-gold-500/40 hover:bg-gold-500/[0.04] transition-colors px-3 py-3 flex items-center gap-2"
 >
 <Icon className="w-4 h-4 text-gold-400 flex-shrink-0" />
 <span className="text-[13px] font-bold text-white truncate">{label}</span>
 </div>
 ))}
 </div>

 {/* Closing value statement */}
 <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-yellow-400/[0.08] via-zinc-950 to-zinc-950 p-7 sm:p-10 text-center max-w-3xl mx-auto">
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.16em] mb-3">
 <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
 Fast by <NoFeeBrand />
 </div>
 <p className="text-[15px] sm:text-base text-white/85 leading-relaxed">
 Fast by <NoFeeBrand /> is <strong className="text-white">not just a savings tool</strong>. It is a complete restaurant growth and operational intelligence platform.
 </p>
 <p className="mt-3 text-[14px] text-white/65 leading-relaxed">
 Restaurants save money, improve delivery performance, gain visibility, increase customer trust, and maintain full control of their business.
 </p>
 </div>
 </div>
 </section>

 {/* Restaurant Dashboard Benefits — not just savings, a growth platform */}
 <section className="relative pb-24 lg:pb-32" data-testid="dashboard-preview-section">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="rounded-3xl border border-gold-500/25 bg-gradient-to-b from-zinc-950 via-black to-zinc-950 p-7 sm:p-10">
 <div className="text-center mb-8">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 A complete growth platform
 </p>
 <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
 Restaurant Dashboard Benefits
 </h2>
 <p className="mt-4 text-white/65 max-w-2xl mx-auto text-[15px]">
 NoFee is not just a savings platform — it is a complete restaurant growth platform.
 </p>
 </div>
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
 {[
 'Orders',
 'Revenue',
 'Fast Score',
 'Verified On-Time Deliveries',
 'Delivery Performance',
 'Customer Reviews',
 'Repeat Customer Metrics',
 ].map((b) => (
 <div
 key={b}
 className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-4 text-center hover:border-gold-500/40 transition-colors"
 >
 <p className="text-[14px] font-bold text-white">{b}</p>
 </div>
 ))}
 </div>
 </div>
 </div>
 </section>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

const Stat = ({ label, value, muted = false, testId }) => (
 <div
 className={`rounded-2xl border ${muted? 'border-white/[0.06] bg-white/[0.02]': 'border-gold-500/20 bg-gold-500/[0.03]'} px-4 py-3.5`}
 data-testid={`stat-${testId}`}
 >
 <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold">
 {label}
 </p>
 <p
 className={`mt-1 text-xl sm:text-2xl font-black tabular-nums ${
 muted? 'text-white/70': 'text-white'
 }`}
 >
 {value}
 </p>
 </div>
);

const ComparisonCard = ({ title, rate, bullets, isLoss = false }) => (
 <div
 className={`relative rounded-3xl border p-7 sm:p-8 ${
 isLoss
? 'border-white/10 bg-zinc-950'
: 'border-gold-500/30 bg-gradient-to-b from-zinc-950 to-black shadow-[0_30px_80px_-30px_rgba(212,175,55,0.25)]'
 }`}
 >
 <div className="flex items-baseline justify-between mb-6">
 <h3 className="text-2xl font-black text-white">{title}</h3>
 <span
 className={`text-3xl font-black tabular-nums ${isLoss? 'text-rose-300/80': 'text-gold-400'}`}
 >
 {rate}
 </span>
 </div>
 <ul className="space-y-3">
 {bullets.map((b) => (
 <li key={b} className="flex items-start gap-3 text-[14px] text-white/75">
 {isLoss? (
 <Minus className="w-4 h-4 text-rose-400/70 flex-shrink-0 mt-0.5" />
 ): (
 <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
 )}
 <span>{b}</span>
 </li>
 ))}
 </ul>
 {!isLoss && (
 <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2 text-[12px] text-white/55">
 <ShieldCheck className="w-3.5 h-3.5 text-gold-400" />
 <span>Secure direct payouts · Stripe</span>
 </div>
 )}
 </div>
);

export default SavingsCalculatorPage;
